import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Package, Bell, Search, RefreshCw, Loader2, MapPin, AlertTriangle, CheckCircle2, Truck, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pre_transit: { icon: Package, cls: 'bg-slate-100 text-slate-700', label: 'Label created' },
  in_transit: { icon: Truck, cls: 'bg-indigo-100 text-indigo-700', label: 'In transit' },
  out_for_delivery: { icon: Truck, cls: 'bg-amber-100 text-amber-700', label: 'Out for delivery' },
  delivered: { icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700', label: 'Delivered' },
  exception: { icon: AlertTriangle, cls: 'bg-rose-100 text-rose-700', label: 'Exception' },
  return_to_sender: { icon: XCircle, cls: 'bg-orange-100 text-orange-700', label: 'Returning' },
};

const NOTIF_ICON = {
  delivered: CheckCircle2, out_for_delivery: Truck, exception: AlertTriangle, returned: XCircle,
};

export default function TrackingPage() {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };
  const [active, setActive] = useState(null);
  const [notif, setNotif] = useState(null);
  const [q, setQ] = useState('');
  const [lookup, setLookup] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [a, n] = await Promise.all([
      fetch(`${API}/tracking/my/active`, { headers: auth }).then((r) => r.ok ? r.json() : { shipments: [] }),
      fetch(`${API}/notifications?limit=30`, { headers: auth }).then((r) => r.ok ? r.json() : { notifications: [], unread: 0 }),
    ]);
    setActive(a); setNotif(n);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const track = async (tn) => {
    setBusy(true); setLookup(null);
    try {
      const r = await fetch(`${API}/tracking/${encodeURIComponent(tn)}`, { headers: auth });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Track failed');
      setLookup(d);
      load();
    } catch (e) { setLookup({ error: e.message }); }
    finally { setBusy(false); }
  };

  const markRead = async (id) => {
    await fetch(`${API}/notifications/${id}/read`, { method: 'POST', headers: auth });
    load();
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="tracking-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Truck className="w-6 h-6 text-indigo-500" /> Tracking &amp; Notifications
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Live carrier updates + automatic exception alerts.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</Button>
      </div>

      {/* Lookup */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> Track any package</CardTitle></CardHeader>
        <CardContent className="flex gap-2">
          <Input placeholder="Paste tracking number…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && q && track(q)} data-testid="track-input" />
          <Button onClick={() => q && track(q)} disabled={busy || !q} data-testid="track-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Track'}
          </Button>
        </CardContent>
        {lookup && (
          <CardContent className="pt-0">
            {lookup.error ? (
              <p className="text-sm text-rose-600" data-testid="track-error">{lookup.error}</p>
            ) : (
              <div className="border-t pt-4" data-testid="track-result">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-mono">{lookup.tracking_number}</p>
                    <p className="text-xs text-slate-500">{lookup.carrier?.toUpperCase()} · ETA {lookup.est_delivery_date || '—'}</p>
                  </div>
                  {(() => {
                    const s = STATUS_BADGE[lookup.status] || STATUS_BADGE.in_transit;
                    return <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${s.cls}`}><s.icon className="w-3 h-3" />{s.label}</span>;
                  })()}
                </div>
                <ol className="space-y-2 text-xs" data-testid="track-events">
                  {(lookup.events || []).map((e, i) => (
                    <li key={i} className="flex gap-2 border-l-2 border-slate-200 dark:border-slate-700 pl-3 py-1">
                      <span className="w-4 h-4 rounded-full bg-indigo-500 -ml-[9px] shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium">{e.description}</p>
                        <p className="text-slate-500">{e.location} · {new Date(e.datetime).toLocaleString()}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="w-4 h-4" /> Notifications
            {notif?.unread > 0 && <span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold bg-rose-500 text-white rounded-full w-5 h-5">{notif.unread}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!notif ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : notif.notifications.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No notifications yet. Delivery, exception, and return alerts will appear here.</p>
          ) : (
            <div className="space-y-2">
              {notif.notifications.map((n) => {
                const Icon = NOTIF_ICON[n.kind] || Bell;
                return (
                  <div key={n.id} className={`p-3 rounded-lg border flex items-start gap-2 ${n.read ? 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800' : 'bg-white dark:bg-slate-900 border-indigo-200 dark:border-indigo-800'}`} data-testid={`notif-${n.id.slice(0, 8)}`}>
                    <Icon className={`w-4 h-4 mt-0.5 ${n.kind === 'exception' || n.kind === 'returned' ? 'text-rose-500' : n.kind === 'delivered' ? 'text-emerald-500' : 'text-indigo-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{n.message}</p>
                      <p className="text-xs text-slate-500">{n.tracking_number} · {new Date(n.created_at).toLocaleString()}</p>
                    </div>
                    {!n.read && (
                      <button className="text-xs text-indigo-600 hover:underline" onClick={() => markRead(n.id)} data-testid={`markread-${n.id.slice(0, 8)}`}>
                        Mark read
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active shipments */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4" /> Recent shipments</CardTitle></CardHeader>
        <CardContent>
          {!active ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : active.shipments.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No shipments yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-2 py-2">Tracking</th>
                  <th className="text-left px-2 py-2">Carrier</th>
                  <th className="text-left px-2 py-2">To</th>
                  <th className="text-left px-2 py-2">Created</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {active.shipments.slice(0, 30).map((s, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-2 py-2 font-mono text-xs">{s.tracking_number}</td>
                    <td className="px-2 py-2 text-xs uppercase">{s.carrier || '—'}</td>
                    <td className="px-2 py-2 text-xs flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{s.to_zip || '—'}</td>
                    <td className="px-2 py-2 text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                    <td className="px-2 py-2 text-right">
                      <button onClick={() => track(s.tracking_number)} className="text-xs text-indigo-600 hover:underline" data-testid={`track-${s.tracking_number}`}>Track</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400">
        Address changes on in-flight shipments are locked in SelleSend. To reroute, contact the carrier directly through their delivery exception process.
      </p>
    </div>
  );
}
